import { useEffect, useState, useCallback, useMemo } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import UploadIcon from "@mui/icons-material/Upload";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import ImportExcelDialog from "components/Common/ImportExcelDialog";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import PoliciesTableData from "./data/PoliciesTableData";
import { policiesFormSchema, policiesRecord } from "./data/PoliciesSchemas";
import { PoliciesRenewSchemas, policiesRenewRecord } from "./data/PoliciesRenewSchemas";
import { policySearchFormSchema, policySearchRecord } from "./data/PolicySearchSchemas";
import { handleApiError } from "utils/apiError";

function Policies() {
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRenewPolicyOpen, setDialogRenewPolicyOpen] = useState(false);
  const [dialogSearchOpen, setDialogSearchOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(policiesRecord);
  const [editingRecordRenewPolicy, setEditingRecordRenewPolicy] = useState(policiesRenewRecord);
  const [searchFormData, setSearchFormData] = useState(policySearchRecord);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [policyTypesLoading, setPolicyTypesLoading] = useState(false);
  const [policyItems, setPolicyItems] = useState([]);
  const [policyItemsLoading, setPolicyItemsLoading] = useState(false);
  const [insurees, setInsurees] = useState([]);

  const [sb, setSb] = useState({
    open: false,
    title: "",
    type: "error",
    message: "",
  });

  const showSnackBar = (title, type, message) => {
    setSb({ open: true, title, type, message });
  };

  // Load Benefit Items
  const loadPolicyItems = useCallback(
    async (search = "") => {
      try {
        setPolicyItemsLoading(true);
        const url = `${apiBase}/api/policy-items/search-list?page=0&size=50&q=${search}`;
        const res = await httpClient.get(url);
        const data = res.data.content || [];
        setPolicyItems(data.map((item) => ({ ...item, label: item.name })));
      } catch (err) {
        console.error("Failed to load policy items", err);
      } finally {
        setPolicyItemsLoading(false);
      }
    },
    [apiBase]
  );

  // Load Insurees (Beneficiaries)
  const loadInsurees = useCallback(
    async (search = "") => {
      try {
        const url = `${apiBase}/api/insurees/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const data = res.data.content || [];
        setInsurees(
          data.map((i) => ({
            ...i,
            label: `${i.name} (${i.employeeId || "N/A"})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load insurees", err);
      }
    },
    [apiBase]
  );

  const loadCards = useCallback(
    async (search = "") => {
      try {
        setCardsLoading(true);
        const url = `${apiBase}/api/cards/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const cardData = res.data.content || [];
        setCards(
          cardData.map((card) => ({
            ...card,
            label: `${card.code} (${card.description})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load cards", err);
      } finally {
        setCardsLoading(false);
      }
    },
    [apiBase]
  );

  const loadPolicyTypes = useCallback(
    async (search = "") => {
      try {
        setPolicyTypesLoading(true);
        const url = `${apiBase}/api/policy-types/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const policyTypeData = res.data.content || [];
        setPolicyTypes(
          policyTypeData.map((policyType) => ({
            ...policyType,
            label: `${policyType.name} (${policyType.code})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load policy types", err);
      } finally {
        setPolicyTypesLoading(false);
      }
    },
    [apiBase]
  );

  // Initial Loads
  useEffect(() => {
    loadPolicyItems();
    loadInsurees();
    loadCards();
    loadPolicyTypes();
  }, [loadPolicyItems, loadInsurees, loadCards, loadPolicyTypes]);

  // Memoized dependencies for the Dialog Form
  const formDeps = useMemo(
    () => ({
      cards,
      cardsLoading,
      onSearchCards: loadCards,
      policyTypes,
      policyTypesLoading,
      onSearchPolicyTypes: loadPolicyTypes,
      availablePolicyItems: policyItems,
      policyItemsLoading,
      onSearchPolicyItems: loadPolicyItems,
      insurees,
      onSearchInsurees: loadInsurees,
    }),
    [
      cards,
      cardsLoading,
      policyTypes,
      policyTypesLoading,
      policyItems,
      policyItemsLoading,
      insurees,
      loadCards,
      loadPolicyTypes,
      loadPolicyItems,
      loadInsurees,
    ]
  );

  const loadPolicies = async (searchParams = null) => {
    try {
      setLoading(true);
      let url;
      if (searchParams && Object.values(searchParams).some((val) => val && val !== "")) {
        // If we have search parameters, use the search endpoint with POST
        url = `${apiBase}/api/policies/search?page=${page}&size=${rowsPerPage}`;
        const response = await httpClient.post(url, searchParams);
        setItems(response.data.content || []);
        setTotalElements(response.data.totalElements || 0);
      } else {
        // Otherwise use regular pagination
        url = searchInput
          ? `${apiBase}/api/policies/search-list?page=${page}&size=${rowsPerPage}&q=${searchInput}`
          : `${apiBase}/api/policies?page=${page}&size=${rowsPerPage}`;
        const res = await httpClient.get(url);
        setItems(res.data.content || []);
        setTotalElements(res.data.totalElements || 0);
      }
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, [page, rowsPerPage]);

  const onAdvancedSearch = async (formData) => {
    setSaving(true);
    try {
      // Prepare the search payload
      const payload = {
        code: formData.code || null,
        employerName: formData.employerName || null,
        status: formData.status || null,
        policyTypeId: formData.policyTypeId || null,
        cardCode: formData.cardCode || null,
        effectiveDateFrom: formData.effectiveDateFrom
          ? formData.effectiveDateFrom + "T00:00:00"
          : null,
        effectiveDateTo: formData.effectiveDateTo ? formData.effectiveDateTo + "T23:59:59" : null,
        expiryDateFrom: formData.expiryDateFrom ? formData.expiryDateFrom + "T00:00:00" : null,
        expiryDateTo: formData.expiryDateTo ? formData.expiryDateTo + "T23:59:59" : null,
      };

      const url = `${apiBase}/api/policies/display-search-filtered?page=0&size=${rowsPerPage}`;
      const response = await httpClient.post(url, payload);

      setItems(response.data.content || []);
      setTotalElements(response.data.totalElements || 0);
      setPage(0); // Reset to first page
      showSnackBar("Success", "success", `Found ${response.data.totalElements || 0} policies`);
      setDialogSearchOpen(false);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onExportToExcel = async (formData) => {
    setSaving(true);
    try {
      const url = `${apiBase}/api/policies/export/excel`;
      const payload = {
        code: formData.code || null,
        employerName: formData.employerName || null,
        status: formData.status || null,
        policyTypeId: formData.policyTypeId || null,
        cardCode: formData.cardCode || null,
        effectiveDateFrom: formData.effectiveDateFrom
          ? formData.effectiveDateFrom + "T00:00:00"
          : null,
        effectiveDateTo: formData.effectiveDateTo ? formData.effectiveDateTo + "T23:59:59" : null,
        expiryDateFrom: formData.expiryDateFrom ? formData.expiryDateFrom + "T00:00:00" : null,
        expiryDateTo: formData.expiryDateTo ? formData.expiryDateTo + "T23:59:59" : null,
      };

      const response = await httpClient.post(url, payload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `Policies_Export_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showSnackBar("Export Successful", "success", "Your file is downloading.");
      setDialogSearchOpen(false);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onEditingRecordChange = async (rec) => {
    try {
      setLoading(true);
      const res = await httpClient.get(`${apiBase}/api/policies/${rec.id}`);
      const fullData = res.data;

      const preparedData = {
        ...fullData,
        policyTypeId: fullData.policyTypeId || null,
        cardId: fullData.cardId || null,
        insureeIds: Array.isArray(fullData.insureeIds) ? fullData.insureeIds : [],
        benefits: fullData.benefits || [],
      };

      if (fullData.cardId) {
        loadCards(fullData.cardId);
      }

      if (fullData.policyTypeId) {
        loadPolicyTypes(fullData.policyTypeId);
      }

      setEditingRecord(preparedData);
      setDialogOpen(true);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        policyTypeId: data.policyTypeId || null,
        cardId: data.cardId || null,
      };

      const isEdit = payload.id && Number(payload.id) > 0;
      const url = isEdit ? `${apiBase}/api/policies/${payload.id}` : `${apiBase}/api/policies`;

      await httpClient[isEdit ? "put" : "post"](url, payload);

      showSnackBar("Success", "success", `Policy ${isEdit ? "updated" : "created"}`);
      setDialogOpen(false);
      loadPolicies();
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onSubmitBulkUpload = async (data) => {
    setSaving(true);
    try {
      const formData = new FormData();
      const { bulk_upload_file, ...restData } = data;

      const requestData = {
        mode: data.mode,
        expiryDatetime: data.mode === "Renew" ? data.expiryDatetime + "T23:59:59" : null,
        benefits: data.mode === "Renew" ? data.benefits : [],
      };

      formData.append("data", JSON.stringify(requestData));

      if (bulk_upload_file) {
        formData.append("file", bulk_upload_file);
      }

      const url = `${apiBase}/api/policies/renewOrDeactivations`;
      await httpClient.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showSnackBar("Success", "success", "Bulk operation completed successfully");
      setDialogRenewPolicyOpen(false);
      loadPolicies();
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onDeleteConfirmed = async () => {
    try {
      if (Array.isArray(itemToDelete)) {
        await httpClient.post(`${apiBase}/api/policies/bulk-delete`, { ids: itemToDelete });
      } else {
        await httpClient.delete(`${apiBase}/api/policies/${itemToDelete.id}`);
      }
      showSnackBar("Success", "success", "Deleted successfully");
      loadPolicies();
      setSelectedIds([]);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  const onCreateNewRecord = () => {
    const defaultBenefits = policyItems.map((item) => ({
      id: null,
      policyItemId: item.id,
      limit: 0,
      countLimit: 0,
    }));

    setEditingRecord({
      ...policiesRecord,
      benefits: defaultBenefits,
      insurees: [],
    });

    setDialogOpen(true);
  };

  const onCreateBulkUpload = () => {
    const defaultBenefits = policyItems.map((item) => ({
      id: null,
      policyItemId: item.id,
      limit: 0,
      countLimit: 0,
    }));

    setEditingRecordRenewPolicy({
      ...policiesRenewRecord,
      mode: "Renew",
      benefits: defaultBenefits,
    });

    setDialogRenewPolicyOpen(true);
  };

  const downloadTemplate = async () => {
    try {
      const response = await httpClient.get(
        `${apiBase}/api/policies/template-renew-or-activations`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `policy_${editingRecordRenewPolicy.mode.toLowerCase()}_template.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    }
  };

  const handleSearchDialogClose = () => {
    setDialogSearchOpen(false);
    setSearchFormData({
      ...policySearchRecord,
      externalErrors: {},
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" p={3}>
            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="info" size="small" onClick={onCreateNewRecord}>
                Create Policy <Icon>add</Icon>
              </MDButton>
              <MDButton
                variant="gradient"
                color="warning"
                size="small"
                onClick={onCreateBulkUpload}
              >
                Activations/Renewals <UploadIcon fontSize="small" />
              </MDButton>
              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                onClick={() => setImportOpen(true)}
              >
                Bulk Import <UploadIcon fontSize="small" />
              </MDButton>
            </MDBox>
            <MDBox display="flex" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => {
                  setSearchFormData({ ...policySearchRecord });
                  setDialogSearchOpen(true);
                }}
              >
                Advanced Search
              </MDButton>
              <MDInput
                size="small"
                label="Search Policies"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadPolicies()}
              />
              <MDButton variant="outlined" color="info" size="small" onClick={() => loadPolicies()}>
                Go
              </MDButton>
              <CustomMenuButton
                buttonTitle="Actions"
                items={[
                  {
                    key: "del",
                    text: "Delete Selected",
                    icon: "delete",
                    iconColor: "error",
                  },
                ]}
                onItemClicked={() => {
                  if (selectedIds.length > 0) {
                    setItemToDelete(selectedIds);
                    setConfirmationDialogOpen(true);
                  } else {
                    showSnackBar("No Selection", "warning", "Please select policies to delete");
                  }
                }}
              />
            </MDBox>
          </MDBox>

          <MDBox px={3} pb={3}>
            {loading ? (
              <MDTypography variant="button">Loading...</MDTypography>
            ) : (
              <PoliciesTableData
                data={items}
                selectedIds={selectedIds}
                onToggle={(id) =>
                  setSelectedIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                  )
                }
                onEdit={onEditingRecordChange}
                onDelete={(rec) => {
                  setItemToDelete(rec);
                  setConfirmationDialogOpen(true);
                }}
              />
            )}
            <CustomTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={totalElements}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        title="Policy Details"
        schema={policiesFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={onSubmit}
        deps={formDeps}
      />

      <SchemaDialogForm
        open={dialogRenewPolicyOpen}
        title="Bulk Upload Policies"
        schema={PoliciesRenewSchemas}
        initialRecord={editingRecordRenewPolicy}
        saving={saving}
        onClose={() => setDialogRenewPolicyOpen(false)}
        onSubmit={onSubmitBulkUpload}
        deps={{
          ...formDeps,
          // Add the download function directly
          onDownloadTemplate: downloadTemplate,
        }}
      />

      <SchemaDialogForm
        open={dialogSearchOpen}
        title="Advanced Search Policies"
        schema={policySearchFormSchema}
        initialRecord={searchFormData}
        saving={saving}
        onClose={handleSearchDialogClose}
        onSubmit={onExportToExcel}
        deps={{
          policyTypes,
          policyTypesLoading,
          onSearchPolicyTypes: loadPolicyTypes,
          buttons: [
            {
              index: 1,
              label: "Search",
              color: "primary",
              variant: "gradient",
              onClick: onAdvancedSearch,
            },
          ],
        }}
        submitButtonText="Export to Excel"
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete the selected policy/policies?"
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />

      <ImportExcelDialog
        open={importOpen}
        title="Import Policies"
        endpoint={`${apiBase}/api/policies/import`}
        templateEndpoint={`${apiBase}/api/policies/template`}
        onClose={() => setImportOpen(false)}
        onDone={() => loadPolicies()}
        showSnackBar={showSnackBar}
      />

      <MDSnackbar
        color={sb.type}
        icon="notifications"
        title={sb.title}
        content={sb.message}
        open={sb.open}
        onClose={() => setSb({ ...sb, open: false })}
      />
    </DashboardLayout>
  );
}

export default Policies;
